export type RealtimeTrackGeometry = {
    bbox: number[];
    centerX: number;
    centerY: number;
    torsoX: number;
    torsoY: number;
    shoulderX?: number;
    shoulderY?: number;
    hipX?: number;
    hipY?: number;
};

export type RealtimeTrackMatch = {
    admissible: boolean;
    score: number;
    bboxIou: number;
    centerDistance: number;
    torsoDistance: number;
    shoulderDistance: number;
    hipDistance: number;
    minimumTrackIou: number;
    postureChange: boolean;
};

export function normalizedBboxIou(a: number[], b: number[]): number {
    if (a.length < 4 || b.length < 4) return 0;
    const intersection = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]))
        * Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
    const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
    const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
    return intersection / Math.max(1e-9, areaA + areaB - intersection);
}

export function rapidPostureChange(previousBbox: number[], nextBbox: number[]): boolean {
    if (previousBbox.length < 4 || nextBbox.length < 4) return false;
    const previousWidth = Math.max(0.001, previousBbox[2] - previousBbox[0]);
    const previousHeight = Math.max(0.001, previousBbox[3] - previousBbox[1]);
    const nextWidth = Math.max(0.001, nextBbox[2] - nextBbox[0]);
    const nextHeight = Math.max(0.001, nextBbox[3] - nextBbox[1]);
    const previousAspect = previousWidth / previousHeight;
    const nextAspect = nextWidth / nextHeight;
    return (previousAspect < 0.75 && nextAspect > 1.05)
        || Math.abs(Math.log(nextAspect / previousAspect)) >= 0.48;
}

export function scoreRealtimeTrackMatch(
    track: RealtimeTrackGeometry & { velocityX: number; velocityY: number },
    candidate: RealtimeTrackGeometry,
    ageMs: number,
    recoveryWindowMs: number,
): RealtimeTrackMatch {
    const predictedX = track.centerX + track.velocityX * ageMs;
    const predictedY = track.centerY + track.velocityY * ageMs;
    const centerDistance = Math.hypot(candidate.centerX - predictedX, candidate.centerY - predictedY);
    const torsoDistance = Math.hypot(candidate.torsoX - track.torsoX, candidate.torsoY - track.torsoY);
    const shoulderDistance = Number.isFinite(track.shoulderX) && Number.isFinite(candidate.shoulderX)
        ? Math.hypot(Number(candidate.shoulderX) - Number(track.shoulderX), Number(candidate.shoulderY) - Number(track.shoulderY))
        : torsoDistance;
    const hipDistance = Number.isFinite(track.hipX) && Number.isFinite(candidate.hipX)
        ? Math.hypot(Number(candidate.hipX) - Number(track.hipX), Number(candidate.hipY) - Number(track.hipY))
        : torsoDistance;
    const bboxIou = normalizedBboxIou(track.bbox, candidate.bbox);
    const postureChange = rapidPostureChange(track.bbox, candidate.bbox);
    const minimumTrackIou = postureChange ? 0.10 : 0.25;
    const centerGate = postureChange ? 0.34 : 0.24;
    const admissible = ageMs <= recoveryWindowMs && (
        bboxIou >= minimumTrackIou
        || centerDistance <= centerGate
        || torsoDistance <= (postureChange ? 0.30 : 0.20)
        || shoulderDistance <= (postureChange ? 0.32 : 0.20)
        || hipDistance <= (postureChange ? 0.32 : 0.20)
    );
    const stalePenalty = Math.min(0.18, ageMs / Math.max(1, recoveryWindowMs) * 0.18);
    return {
        admissible,
        score: centerDistance * 0.38 + torsoDistance * 0.16 + shoulderDistance * 0.10
            + hipDistance * 0.11 + (1 - bboxIou) * 0.20 + stalePenalty,
        bboxIou,
        centerDistance,
        torsoDistance,
        shoulderDistance,
        hipDistance,
        minimumTrackIou,
        postureChange,
    };
}

export function retainedPoseState(poseAgeMs: number, poseHoldMs: number): 'HELD' | 'PREDICTED' | 'LOST' {
    if (poseAgeMs <= poseHoldMs * 0.5) return 'HELD';
    return poseAgeMs <= poseHoldMs ? 'PREDICTED' : 'LOST';
}
